import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

/**
 * Home — the "what do you want to do today?" landing screen.
 *
 * Purpose: give a first-time user 4 obvious doors instead of the sidebar's
 * 12 items. Every card is a verb + benefit, not a feature name.
 *
 * We also pull today's totals from the macros endpoint so the top strip
 * gives a personal snapshot ("You've eaten 420 of 2143 kcal today").
 */
export default function Home() {
  const { user } = useAuth();
  const [today, setToday] = useState(null);
  const [forYou, setForYou] = useState(null);
  const navigate = useNavigate();

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/macros/today");
        setToday(data);
      } catch { /* silent */ }
    })();

    // ---- Home rail: hydrate from cache first, then refresh in background ----
    const CACHE_KEY = "home_foryou_v1";
    const TTL_MS = 2 * 60 * 60 * 1000; // 2 hours
    try {
      const raw = localStorage.getItem(CACHE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && Date.now() - (parsed.savedAt || 0) < TTL_MS && Array.isArray(parsed.meals) && parsed.meals.length) {
          // Show cached instantly — no loading skeleton for returning users.
          setForYou({ loading: false, meals: parsed.meals, context: parsed.context || null, reason: null });
        }
      }
    } catch { /* localStorage may be blocked */ }

    (async () => {
      // If cache didn't hydrate, mark as loading so the skeleton shows.
      setForYou((cur) => cur ? cur : { loading: true, meals: [] });
      try {
        const { data } = await api.get("/healthy-meals/for-you", { timeout: 35000 });
        const next = {
          loading: false,
          meals: data.meals || [],
          context: data.context || null,
          reason: data.personalised === false ? (data.reason || "no_profile") : null,
        };
        setForYou(next);
        // Persist for the next visit (only when we actually got meals)
        if (next.meals.length) {
          try {
            localStorage.setItem(CACHE_KEY, JSON.stringify({
              meals: next.meals,
              context: next.context,
              savedAt: Date.now(),
            }));
          } catch { /* quota / private mode */ }
        }
      } catch {
        setForYou((cur) => cur && cur.meals.length ? cur : { loading: false, meals: [] });
      }
    })();
  }, []);

  const firstName = (user?.name || user?.email || "").split(" ")[0].split("@")[0];
  const kcalTarget = today?.targets?.kcal || 0;
  const kcalTotal = today?.totals?.kcal || 0;
  const kcalLeft = Math.max(0, kcalTarget - kcalTotal);

  return (
    <div className="max-w-5xl mx-auto" data-testid="home-page">
      {/* Greeting + personal snapshot */}
      <div className="mb-8">
        <div className="text-[10px] uppercase tracking-wide text-[#8B9690]">Welcome back</div>
        <h1 className="text-3xl sm:text-4xl font-semibold mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
          Hey {firstName || "there"} 👋
        </h1>
        <p className="text-sm text-[#5C6B61] mt-1">
          {kcalTarget ? (
            <>
              You&apos;ve got <strong className="text-[#3B5A3E]">{kcalLeft} kcal</strong> left today.
              What do you want to do?
            </>
          ) : (
            <>What do you want to do today?</>
          )}
        </p>
      </div>

      {/* 4 big intent cards */}
      <div className="grid sm:grid-cols-2 gap-4" data-testid="intent-cards">
        <IntentCard
          to="/app/planner"
          testid="intent-plan"
          emoji="🥗"
          title="Plan this week's meals"
          hint="Fill your Mon–Sun planner in one place"
          bg="linear-gradient(135deg,#E8F0E9 0%,#F3F0EA 100%)"
        />
        <IntentCard
          to="/app/recipes"
          testid="intent-cook"
          emoji="🍳"
          title="Find & save a recipe"
          hint="From URL, cookbook photo, TikTok caption or AI ideas"
          bg="linear-gradient(135deg,#FBEFEA 0%,#F3F0EA 100%)"
        />
        <IntentCard
          to="/app/shopping"
          testid="intent-shop"
          emoji="🛒"
          title="Build a shopping list"
          hint="Aldi, Lidl, Tesco, Sainsbury's — sends to basket"
          bg="linear-gradient(135deg,#EDF3E9 0%,#F3F0EA 100%)"
        />
        <IntentCard
          to="/app/food-scanner"
          testid="intent-scan"
          emoji="🔬"
          title="Is this food healthy?"
          hint="Scan pack, barcode, or paste a supermarket URL"
          bg="linear-gradient(135deg,#F6D6CE 0%,#F3F0EA 100%)"
        />
      </div>

      {/* Personalised horizontal rail */}
      <ForYouRail state={forYou} onOpenAll={() => navigate("/app/discover")} />

      {/* Secondary quick links (smaller, less prominent) */}
      <div className="mt-8">
        <div className="text-[10px] uppercase tracking-wide text-[#8B9690] mb-3">Also handy</div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <SmallLink to="/app/calories" testid="quick-calories" icon="🎯" label="Calorie target" />
          <SmallLink to="/app/weight" testid="quick-weight" icon="⚖️" label="Weight" />
          <SmallLink to="/app/discover" testid="quick-discover" icon="✨" label="What to eat" />
          <SmallLink to="/app/friends" testid="quick-friends" icon="👥" label="Friends" />
        </div>
      </div>
    </div>
  );
}

function IntentCard({ to, testid, emoji, title, hint, bg }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="rounded-3xl p-6 md:p-7 flex flex-col gap-3 hover:shadow-lg hover:-translate-y-0.5 transition-all min-h-[168px]"
      style={{ background: bg, border: "1px solid #E5E2DA" }}
    >
      <span className="text-4xl md:text-5xl leading-none" aria-hidden>{emoji}</span>
      <div>
        <div className="text-lg md:text-xl font-semibold" style={{ fontFamily: "Outfit, sans-serif", color: "#2A332C" }}>
          {title}
        </div>
        <div className="text-xs md:text-sm text-[#5C6B61] mt-1 leading-snug">
          {hint}
        </div>
      </div>
      <span className="mt-auto text-xs font-medium text-[#3B5A3E]">Open →</span>
    </Link>
  );
}

function SmallLink({ to, testid, icon, label }) {
  return (
    <Link
      to={to}
      data-testid={testid}
      className="rounded-2xl bg-white border border-[#E5E2DA] p-3 flex items-center gap-2 text-sm hover:bg-[#F3F0EA] transition"
    >
      <span className="text-lg" aria-hidden>{icon}</span>
      <span className="font-medium truncate">{label}</span>
    </Link>
  );
}

// -----------------------------------------------------------------------
// Personalised "For you" rail — horizontal scroll of 6 meal cards fetched
// from /api/healthy-meals/for-you. Falls back gracefully to a "set your
// calorie target to unlock" nudge for users without a profile.
// -----------------------------------------------------------------------
function ForYouRail({ state, onOpenAll }) {
  if (!state) return null;

  if (state.reason === "no_profile") {
    return (
      <section className="mt-10" data-testid="foryou-rail-nudge">
        <div className="ff-card p-5 md:p-6 flex items-center justify-between gap-4"
             style={{ background: "linear-gradient(135deg,#EDF3E9 0%,#F3F0EA 100%)" }}>
          <div>
            <div className="text-[10px] uppercase tracking-wide text-[#3B5A3E]">Unlock personalised meals</div>
            <h3 className="text-lg font-medium mt-1" style={{ fontFamily: "Outfit, sans-serif" }}>
              Tell us your daily calorie target and we&apos;ll suggest meals that fit
            </h3>
          </div>
          <Link to="/app/calories" className="ff-btn-primary whitespace-nowrap">Set target</Link>
        </div>
      </section>
    );
  }

  if (state.loading) {
    return (
      <section className="mt-10" data-testid="foryou-rail-loading">
        <div className="flex items-center justify-between mb-3">
          <h2 className="text-lg font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>For you tonight</h2>
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[1,2,3,4].map(i => (
            <div key={i} className="min-w-[220px] h-44 rounded-2xl bg-[#F3F0EA] animate-pulse" />
          ))}
        </div>
      </section>
    );
  }

  if (!state.meals || state.meals.length === 0) return null;

  const ctx = state.context || {};
  const slotLabel = ctx.meal_slot === "breakfast" ? "For breakfast" : ctx.meal_slot === "lunch" ? "For lunch" : "For you tonight";

  return (
    <section className="mt-10" data-testid="foryou-rail">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h2 className="text-lg md:text-xl font-semibold" style={{ fontFamily: "Outfit, sans-serif" }}>
            {slotLabel}
          </h2>
          {ctx.remaining_kcal ? (
            <div className="text-xs text-[#5C6B61] mt-0.5">
              Meals that fit your {ctx.remaining_kcal} kcal left today
            </div>
          ) : null}
        </div>
        <button
          type="button"
          onClick={onOpenAll}
          data-testid="foryou-rail-see-all"
          className="text-sm font-medium text-[#3B5A3E] hover:underline whitespace-nowrap"
        >
          See all →
        </button>
      </div>

      <div
        className="flex gap-3 overflow-x-auto pb-2 -mx-1 px-1 snap-x snap-mandatory"
        style={{ scrollbarWidth: "thin" }}
        data-testid="foryou-rail-scroll"
      >
        {state.meals.map((m) => (
          <ForYouCard key={m.temp_id} meal={m} />
        ))}
      </div>
    </section>
  );
}

function ForYouCard({ meal }) {
  const kcal = meal.calories_per_serving;
  const protein = meal.protein_g;
  return (
    <Link
      to="/app/discover"
      data-testid={`foryou-rail-card-${meal.temp_id}`}
      className="min-w-[220px] max-w-[220px] snap-start rounded-2xl overflow-hidden bg-white border border-[#E5E2DA] hover:shadow-md transition"
    >
      {meal.image ? (
        <img src={meal.image} alt="" className="w-full h-28 object-cover" />
      ) : (
        <div className="w-full h-28 bg-[#F3F0EA]" />
      )}
      <div className="p-3">
        <div className="text-sm font-medium leading-snug line-clamp-2 min-h-[2.6em]" style={{ fontFamily: "Outfit, sans-serif" }}>
          {meal.title}
        </div>
        <div className="flex flex-wrap gap-1.5 mt-2">
          {kcal != null && (
            <span className="ff-chip text-[10px]" style={{ background: "#FBEFEA", color: "#A65541" }}>
              🔥 {kcal} kcal
            </span>
          )}
          {protein != null && protein > 0 && (
            <span className="ff-chip text-[10px]" style={{ background: "#F6D6CE", color: "#A33D26" }}>
              {Math.round(protein)}g protein
            </span>
          )}
        </div>
      </div>
    </Link>
  );
}
